import ActionsButton                     from '@/components/actionsButton/ActionsButton';
import { Box }                           from '@chakra-ui/react';
import React                             from 'react';
import { Text }                          from '@chakra-ui/react';
import { useTranslation }                from 'react-i18next';
import ContactModel                      from '@/models/services/network/ContactModel';
import RelationRequestModel              from '@/models/services/network/RelationRequestModel';
import ApiCollection                     from '@widesk-core/models/ApiCollection';
import { Tag }                           from "@/components/ui/tag";
import { getLocale }                     from '@widesk-core/locale';
import { ModalData }                     from '@/components/modal/Modal';
import ContactForm                       from '../forms/ContactForm';
import { toaster }                       from '@/components/ui/toaster';
import RelationContactModel              from '@/models/services/network/RelationContact';
import { HiCheck }                       from "react-icons/hi";
import useScreenSize                     from '@/hooks/useScreenSize';
import RelationRequestStatusHistoryModel from '@/models/services/network/RelationRequestStatusHistoryModel';
import RelationRequestStatusModel        from '@/models/services/network/RelationRequestStatusModel';
import _capitalize                       from "lodash/capitalize";
import FileModel                         from '@/models/services/file/FileModel';
import Image                             from '@/components/Image/Image';
import useFile                           from '@/hooks/useFile';
import RelationStatusModel               from '@/models/services/network/RelationStatus';
import RelationStatusHistoryModel        from '@/models/services/network/RelationStatusHistoryModel';
import RelationModel                     from '@/models/services/network/RelationModel';
import { observer }                      from 'mobx-react';

interface IContactListRowProps {
	contact: ContactModel;
	contactCollection: ApiCollection<ContactModel>;
	fileCollection: ApiCollection<FileModel>;
	relationRequestCollection: ApiCollection<RelationRequestModel>;
	relationRequestStatusCollection: ApiCollection<RelationRequestStatusModel>;
	relationContactCollection: ApiCollection<RelationContactModel>;
	relationStatusCollection: ApiCollection<RelationStatusModel>;
	setModalData: React.Dispatch<React.SetStateAction<ModalData | undefined>>;
	setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const ContactListRow = observer((props: IContactListRowProps) => {

	const { 
		contact, 
		contactCollection, 
		fileCollection, 
		relationRequestCollection, 
		relationRequestStatusCollection, 
		relationContactCollection, 
		relationStatusCollection, 
		setModalData, 
		setModalIsOpen 
	} = props;

	const { isLG } = useScreenSize();
	const file = fileCollection.find(m => m.typeSource.source.entityUrn === contact.urn) || undefined;
	const { source, sourceIsLoading } = useFile(file);

	const relationRequest = relationRequestCollection.find(m => m.getUrn('contact') === contact.urn);
	const relation = relationContactCollection.find(m => m.getUrn('contact') === contact.urn)?.relation;
	const hasRelationRequest = !!relationRequest;
	const hasRelation = !!relation;
	
	const { t } = useTranslation();

	const editContact = async (contact: ContactModel) => {
		setModalData({
			model: contact,
			setIsOpen: setModalIsOpen,
			titleComponent: <Text>{t('pages.contacts.forms.contactForm.title', { action: t('pages.contacts.list.row.actions.edit.label') })}</Text>,
			bodyComponent: <ContactForm
				contact={contact} 
				file={file}
				existingSource={source}
				setIsOpen={setModalIsOpen}
				setModalData={setModalData}
				onSuccess={async () => {
					await contactCollection.list();
					await fileCollection.setFilter('typeSource.type.reference', 'contact_photo').setSort('createdAt', 'desc').listBy(contactCollection.urns, 'typeSource.source.entityUrn');
					await fileCollection.whenIsLoaded(m => m.typeSource.source);
					setModalIsOpen(false);
					setModalData(undefined);
				}}
			/>,
			onClose: () => {
				setModalIsOpen(false);
				setModalData(undefined);
			},
		})
		setModalIsOpen(true);
	};

	const cancelRelationRequest = async (relationRequest: RelationRequestModel) => {
		setModalData({
			isDangerous: true,
			setIsOpen: setModalIsOpen,
			titleComponent: <Text>{t('pages.contacts.modals.relationRequestCancel.title')}</Text>,
			bodyComponent: <Text>{t('pages.contacts.modals.relationRequestCancel.message', { fullname: contact?.fullName })}</Text>,
			onClose: () => {
				setModalIsOpen(false);
				setModalData(undefined);
			},
			onConfirm: async () => {
				const cancelStatus = relationRequestStatusCollection.find(m => m.reference === 'canceled') as RelationRequestStatusModel;

				const history = new RelationRequestStatusHistoryModel();
				
				await history.patch({
					relationRequest: relationRequest.urn,
					relationRequestStatus: cancelStatus.urn,
				});
			},
			onError: () => {
				toaster.create({
					title: t('pages.contacts.list.row.actions.cancelRelationRequest.messages.error'),
					type: 'error'
				});
				setModalIsOpen(false);
				setModalData(undefined);
			},
			onSuccess: async () => {
				toaster.create({
					title: t('pages.contacts.list.row.actions.cancelRelationRequest.messages.success'),
					type: 'success'
				});
				await relationRequestCollection.setFilter('contact', contactCollection.map(m => m.id)).list();
				await relationRequestCollection.whenIsLoaded(m => m.relationRequestStatus);
				setModalIsOpen(false);
				setModalData(undefined);
			}
		})
		setModalIsOpen(true);
	};

	const sendRelationRequest = async (contact: ContactModel) => {
		setModalData({
			model: contact,
			setIsOpen: setModalIsOpen,
			titleComponent: <Text>{t('pages.contacts.modals.relationRequestConfirm.title')}</Text>,
			bodyComponent: <Text>{t('pages.contacts.modals.relationRequestConfirm.message', { email: contact?.email })}</Text>,
			onClose: () => {
				setModalIsOpen(false);
				setModalData(undefined);
			},
			onConfirm: async () => {
				const newRelationRequest = new RelationRequestModel();
				
				await newRelationRequest.patch({
					contact: contact.urn,
					email: contact.email,
				});
			},
			onError: () => {
				toaster.create({
					title: t('pages.contacts.list.row.actions.sendRelationRequest.messages.error'),
					type: 'error',
					duration: 5000,
				});
				setModalIsOpen(false);
				setModalData(undefined);
			},
			onSuccess: async () => {
				toaster.create({
					title: t('pages.contacts.list.row.actions.sendRelationRequest.messages.success'),
					type: 'success',
					duration: 5000,
				});
				await relationRequestCollection.setFilter('contact', contactCollection.map(m => m.id)).list();
				await relationRequestCollection.whenIsLoaded(m => m.relationRequestStatus);
				setModalIsOpen(false);
				setModalData(undefined);
			}
		})
		setModalIsOpen(true);
	};

	const removeContact = async (contact: ContactModel) => {
		setModalData({
			model: contact,
			isDangerous: true,
			setIsOpen: setModalIsOpen,
			titleComponent: <Text>{t('pages.contacts.modals.removeContactConfirm.title')}</Text>,
			bodyComponent: <Text>{t('pages.contacts.modals.removeContactConfirm.message', { fullname: contact?.fullName })}</Text>,
			onClose: () => {
				setModalIsOpen(false);
				setModalData(undefined);
			},
			onConfirm: async () => {
				await contact.destroy();
			},
			onError: () => {
				toaster.create({
					title: t('pages.contacts.list.row.actions.remove.messages.error'),
					type: 'error'
				});
				setModalIsOpen(false);
				setModalData(undefined);
			},
			onSuccess: async () => {
				toaster.create({
					title: t('pages.contacts.list.row.actions.remove.messages.success'),
					type: 'success'
				});
				await contactCollection.list();
				setModalIsOpen(false);
				setModalData(undefined);
			}
		})
		setModalIsOpen(true);
	};	

	const dissociateContact = async (relation: RelationModel) => {
		setModalData({
			isDangerous: true,
			setIsOpen: setModalIsOpen,
			titleComponent: <Text>{t('pages.contacts.modals.dissociateContact.title')}</Text>,
			bodyComponent: <Text>{t('pages.contacts.modals.dissociateContact.message', { fullname: contact?.fullName })}</Text>,
			onClose: () => {
				setModalIsOpen(false);
				setModalData(undefined);
			},
			onConfirm: async () => {
				const deleteStatus = relationStatusCollection.find(m => m.reference === 'deleted') as RelationStatusModel;

				const history = new RelationStatusHistoryModel();
				
				await history.patch({
					relation: relation.urn,
					relationStatus: deleteStatus.urn,
				});
			},
			onError: () => {
				toaster.create({
					title: t('pages.contacts.list.row.actions.dissociateContact.messages.error'),
					type: 'error'
				});
				setModalIsOpen(false);
				setModalData(undefined);
			},
			onSuccess: async () => {
				toaster.create({
					title: t('pages.contacts.list.row.actions.dissociateContact.messages.success'),
					type: 'success'
				});
				await relationContactCollection.list();
				await relationContactCollection.whenIsLoaded(m => m.relation);
				setModalIsOpen(false);
				setModalData(undefined);
			}
		})
		setModalIsOpen(true);
	};

	const TagComponent = () => {
		if (hasRelation) {
			const text = t(`pages.contacts.${isLG ? 'isAssociated' : 'associated'}`);
			return <Tag
				colorPalette={'green'} 
				startElement={<HiCheck />} 
				size="md"
			>
				<Text textWrap="wrap">{text}</Text>
			</Tag>
		}
		if (hasRelationRequest) {
			const text = isLG ? `${t('pages.contacts.relationRequest')} ${relationRequest.relationRequestStatus.getLabel((getLocale() as 'en' | 'fr'))}` : _capitalize(`${relationRequest.relationRequestStatus.getLabel((getLocale() as 'en' | 'fr'))}`);
			return <Tag
				colorPalette={relationRequest.relationRequestStatus.color} 
				size="md"
			>
				<Text textWrap="wrap">{text}</Text>
			</Tag>
		}
		return <></>
	}

	return (
		<Box
			display="flex"
			alignItems="center"
			justifyContent="space-between"
			overflow="auto"
		>
			<Box
				display="flex"
				alignItems="center"
				gap="25px"
			>
				<Box 
					minWidth="75px" 
					maxWidth="75px" 
					height="75px"
				>
					<Image
						errorSize='xl'
						src={source}
						sourceIsLoading={sourceIsLoading}
						type='profile'
						height="75px"
						width="75px"
						borderRadius="8px"
					/>
				</Box>
				<Box
					display="flex"
					flexDirection="column"
					gap="5px"
					paddingRight="25px"
					minWidth="135px"
				>
					<Box><Text className="poppins-semibold" fontSize="16px" textDecoration="underline">{contact.fullName}</Text></Box>
					<Box fontSize="12px" maxWidth={{ base: 120, lg: "unset" }}>
						<Text>{contact.email}</Text>
					</Box>
					{
						!isLG && <Box><TagComponent /></Box>
					}
				</Box>
			</Box>
			<Box
				display="flex"
				alignItems="center"
				gap="25px"
			>
				{
					isLG && <TagComponent />
				}
				<ActionsButton actions={
					hasRelation
					? [
						{
							label: t('pages.contacts.list.row.actions.dissociateContact.label'), 
							type: 'remove',
							do: () => dissociateContact(relation),
						},
					]
					: [
						{ 
							label: t('pages.contacts.list.row.actions.edit.label'), 
							do: () => editContact(contact),
						},
						{
							disabled: hasRelationRequest,
							label: t('pages.contacts.list.row.actions.sendRelationRequest.label'), 
							do: () => sendRelationRequest(contact),
						},
						{
							disabled: !hasRelationRequest || relationRequest?.relationRequestStatus.reference !== 'pending',
							label: t('pages.contacts.list.row.actions.cancelRelationRequest.label'), 
							do: () => relationRequest && cancelRelationRequest(relationRequest),
						},
						{
							label: t('pages.contacts.list.row.actions.remove.label'), 
							type: 'remove',
							do: () => removeContact(contact),
						},
					]
				}/>
			</Box>
		</Box>
	)
})

export default ContactListRow;